import { useMutation } from "@apollo/client";
import CheckCircle from "@mui/icons-material/CheckCircle";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import OpenInNew from "@mui/icons-material/OpenInNew";
import UploadFile from "@mui/icons-material/UploadFile";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dropzone } from "components/Dropzone";
import { FacilityPicker } from "components/FacilityPicker";
import { TenantPickerDialog } from "components/TenantPickerDialog";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { gql } from "generated-graphql";
import { DocumentType, FacilityPickerFragment } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useTenant } from "hooks/useTenant";
import { useUpsertDocument } from "hooks/useUpsertDocument";
import pluralize from "pluralize";
import { useCallback, useState } from "react";
import { uploadFileDirectly } from "util/uploadFile";

const QUEUE_MANIFEST_DOCUMENT_PARSE = gql(`
  mutation QueueManifestDocumentParse($input: QueueManifestDocumentParseInput!) {
    queueManifestDocumentParse(input: $input) {
      id
      status
    }
  }
`);

type FileUploadStatus = {
  fileName: string;
  status: "pending" | "uploading" | "processing" | "success" | "error";
  error?: string;
  documentId?: string;
  jobId?: string;
};

export function ManifestUpload() {
  useBreadcrumb({ label: "Manifest Upload" });
  const alerts = useAlerts();
  const { tenant, tenantId, setCurrentTenantId } = useTenant();
  const { featureFlags } = useFeatureFlags();
  const { upsertDocument } = useUpsertDocument();
  const [queueManifestParse] = useMutation(QUEUE_MANIFEST_DOCUMENT_PARSE);

  const isManifestIngestEnabled = featureFlags[FeatureFlagKey.ManifestDocumentIngest];
  const canUpload = !!tenantId && isManifestIngestEnabled;

  const [selectedFacility, setSelectedFacility] = useState<FacilityPickerFragment | null>(null);
  const [fileStatuses, setFileStatuses] = useState<FileUploadStatus[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showTenantPicker, setShowTenantPicker] = useState(false);

  const updateFileStatusAtIndex = useCallback(
    (index: number, update: Partial<FileUploadStatus>) => {
      setFileStatuses((prev) => prev.map((f, i) => (i === index ? { ...f, ...update } : f)));
    },
    []
  );

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!canUpload) {
        alerts.error(
          !tenantId
            ? "Please select an organization first."
            : "Manifest document ingest is not enabled for this organization."
        );
        return;
      }

      const nonPdfFiles = acceptedFiles.filter((f) => !f.name.toLowerCase().endsWith(".pdf"));
      if (nonPdfFiles.length > 0) {
        alerts.error(
          `Only PDF files are accepted. Rejected: ${nonPdfFiles.map((f) => f.name).join(", ")}`
        );
        return;
      }

      const statuses: FileUploadStatus[] = acceptedFiles.map((f) => ({
        fileName: f.name,
        status: "pending",
      }));
      setFileStatuses(statuses);
      setIsProcessing(true);

      // Upload all files and create document records
      const documentIds: string[] = [];

      for (let i = 0; i < acceptedFiles.length; i++) {
        const file = acceptedFiles[i];
        updateFileStatusAtIndex(i, { status: "uploading" });
        try {
          const storageLink = await uploadFileDirectly(file, {
            tenantId,
          });

          const documentId = await upsertDocument({
            documentData: {
              tenantId,
              title: file.name,
              storageLink,
              documentType: DocumentType.RcraManifest,
              fileExtension: "pdf",
            },
            currentSubmissionDocument: false,
          });

          if (documentId) {
            documentIds.push(documentId);
            updateFileStatusAtIndex(i, { status: "processing", documentId });
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : "Unknown error";
          updateFileStatusAtIndex(i, { status: "error", error: message });
        }
      }

      // Queue manifest parsing for all successfully uploaded documents
      if (documentIds.length > 0) {
        try {
          const { data } = await queueManifestParse({
            variables: {
              input: {
                tenantId,
                documentIds,
                facilityId: selectedFacility?.id,
              },
            },
          });

          const jobs = data?.queueManifestDocumentParse ?? [];

          // Jobs are returned in the same order as documentIds.
          // Map documentId→jobId for lookup.
          const jobIdByDocumentId = new Map(documentIds.map((docId, i) => [docId, jobs[i]?.id]));

          setFileStatuses((prev) =>
            prev.map((f) => {
              if (f.status === "processing" && f.documentId) {
                return { ...f, status: "success", jobId: jobIdByDocumentId.get(f.documentId) };
              }
              return f;
            })
          );
        } catch (err) {
          const message = err instanceof Error ? err.message : "Failed to queue processing";
          setFileStatuses((prev) =>
            prev.map((f) =>
              f.status === "processing" ? { ...f, status: "error", error: message } : f
            )
          );
        }
      }

      setIsProcessing(false);

      // Read latest state to get accurate counts (avoids stale closure)
      setFileStatuses((prev) => {
        const successCount = prev.filter((f) => f.status === "success").length;
        const errorCount = prev.filter((f) => f.status === "error").length;
        if (successCount > 0) {
          alerts.success(
            `Uploaded ${successCount} ${pluralize("manifest", successCount)}. Processing queued.`
          );
        }
        if (errorCount > 0) {
          alerts.error(`${errorCount} ${pluralize("file", errorCount)} failed.`);
        }
        return prev;
      });
    },
    [
      canUpload,
      tenantId,
      alerts,
      updateFileStatusAtIndex,
      upsertDocument,
      queueManifestParse,
      selectedFacility,
    ]
  );

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Manifest Document Upload
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload RCRA manifest PDFs. Documents will be extracted and waste manifest records created
        automatically.
      </Typography>

      <Stack spacing={2} sx={{ mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" fontWeight="medium">
            Organization:
          </Typography>
          {tenant ? (
            <>
              <Typography variant="body2">{tenant.name}</Typography>
              <Button size="small" variant="text" onClick={() => setShowTenantPicker(true)}>
                (change)
              </Button>
            </>
          ) : (
            <Button size="small" variant="outlined" onClick={() => setShowTenantPicker(true)}>
              Select Organization
            </Button>
          )}
        </Stack>
        {tenantId && (
          <Box sx={{ maxWidth: 400 }}>
            <FacilityPicker
              label="Facility (optional)"
              value={selectedFacility}
              onChange={(facility) => setSelectedFacility(facility)}
              error={undefined}
              invalid={false}
              isDirty={false}
              isTouched={false}
              isValidating={false}
              facilityQueryVariant="waste"
              defaultSearchTerm={`tenantId:${tenantId}`}
            />
          </Box>
        )}
      </Stack>

      {!tenantId && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Select an organization to begin uploading manifests.
        </Alert>
      )}

      {tenantId && !isManifestIngestEnabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Manifest document ingest is not enabled for this organization. Enable the
          &quot;manifest-document-ingest&quot; feature flag to upload manifests.
        </Alert>
      )}

      <Dropzone
        label="Drag and drop PDF files here, or click to browse"
        dragActiveText="Drop PDF files here"
        accept=".pdf"
        multiple
        onDrop={handleDrop}
        disabled={!canUpload || isProcessing}
      />

      {fileStatuses.length > 0 && (
        <List dense sx={{ mt: 2 }}>
          {fileStatuses.map((file) => (
            <ListItem key={file.fileName}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {file.status === "pending" && <UploadFile color="disabled" fontSize="small" />}
                {(file.status === "uploading" || file.status === "processing") && (
                  <CircularProgress size={20} />
                )}
                {file.status === "success" && <CheckCircle color="success" fontSize="small" />}
                {file.status === "error" && <ErrorOutline color="error" fontSize="small" />}
              </ListItemIcon>
              <ListItemText
                primary={file.fileName}
                secondary={
                  file.status === "error"
                    ? file.error
                    : file.status === "processing"
                      ? "Queuing extraction..."
                      : undefined
                }
              />
              {file.jobId && (
                <IconButton
                  size="small"
                  href={`/staff/jobs/${file.jobId}`}
                  target="_blank"
                  title="View job"
                >
                  <OpenInNew fontSize="small" />
                </IconButton>
              )}
            </ListItem>
          ))}
        </List>
      )}

      <TenantPickerDialog
        open={showTenantPicker}
        onClose={() => setShowTenantPicker(false)}
        onTenantSelected={(selectedTenant) => {
          setCurrentTenantId(selectedTenant.id);
          setSelectedFacility(null);
          setShowTenantPicker(false);
        }}
      />
    </Box>
  );
}
